import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type Resolver, type UseFormProps, type UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';

/**
 * Convenience hook wrapping `useForm` with the Zod resolver pre-applied.
 *
 * Usage:
 *   const schema = z.object({ name: z.string().min(3).max(50) });
 *   type FormValues = z.infer<typeof schema>;
 *   const form = useZodForm({ schema, defaultValues: { name: '' } });
 *
 * NOTE: We intentionally widen the resolver type via a single controlled cast (NOT `any`) — RHF v7
 * + Zod v3 generic inference under `exactOptionalPropertyTypes: true` does not accept the resolver
 * shape directly. The cast preserves call-site type-safety because the `schema` generic still
 * threads through to the returned `UseFormReturn`.
 */
export function useZodForm<TSchema extends z.ZodTypeAny>(
  props: { schema: TSchema } & Omit<UseFormProps<z.input<TSchema>>, 'resolver'>,
): UseFormReturn<z.input<TSchema>, unknown, z.output<TSchema>> {
  const { schema, ...rest } = props;
  const resolver = zodResolver(schema) as unknown as Resolver<z.input<TSchema>, unknown>;
  return useForm<z.input<TSchema>, unknown, z.output<TSchema>>({
    ...rest,
    resolver,
    mode: rest.mode ?? 'onSubmit',
  });
}
